/**
 * `identity` store —— 身份状态（非敏感字段，锁屏页也要能展示 DID）。
 *
 * 只存 `{did, address, peerId, name, avatarCid}`，**密钥永不进 store**（ARCH §1.2）。
 */

import { create } from 'zustand';
import { container } from '@/services/container';
import type { ErrorCode } from '@shared/errors';
import type { IdentityMeta } from '@shared/types';
import { DEFAULT_AUTO_LOCK_MINUTES, LS_KEYS } from '@/config';
import { createLogger } from '@/lib/logger';

const logger = createLogger('store:identity');

export type IdentityStatus = 'none' | 'locked' | 'unlocked';

export interface IdentityState {
  status: IdentityStatus;
  did: string;
  address: string;
  peerId: string;
  name: string;
  avatarCid?: string;
  bio?: string;
  registered: boolean;
  faucetClaimed: boolean;
  autoLockMinutes: number;
  unlockedAt?: number;
  loading: boolean;
  errorCode?: ErrorCode;

  setLoading(loading: boolean): void;
  setError(code?: ErrorCode): void;
  setUnlocked(meta: IdentityMeta): void;
  setLocked(meta: Pick<IdentityMeta, 'did' | 'address' | 'peerId' | 'name'> & Partial<IdentityMeta>): void;
  setNone(): void;
  patch(meta: Partial<IdentityMeta>): void;
  setAutoLockMinutes(minutes: number): void;
  /** 手动锁定（触发 service 清内存密钥）。 */
  lock(): void;
}

export const useIdentityStore = create<IdentityState>((set, get) => ({
  status: 'none',
  did: '',
  address: '',
  peerId: '',
  name: '',
  registered: false,
  faucetClaimed: false,
  autoLockMinutes: DEFAULT_AUTO_LOCK_MINUTES,
  loading: false,
  errorCode: undefined,

  setLoading: (loading) => set({ loading }),
  setError: (code) => set({ errorCode: code }),

  setUnlocked: (meta) => {
    try {
      localStorage.setItem(LS_KEYS.lastDid, meta.did);
    } catch {
      // 隐私模式忽略
    }
    set({
      status: 'unlocked',
      did: meta.did,
      address: meta.address,
      peerId: meta.peerId,
      name: meta.name,
      avatarCid: meta.avatarCid,
      bio: meta.bio,
      registered: meta.registered,
      faucetClaimed: meta.faucetClaimed,
      unlockedAt: Date.now(),
      errorCode: undefined,
    });
  },

  setLocked: (meta) =>
    set({
      status: 'locked',
      did: meta.did,
      address: meta.address,
      peerId: meta.peerId,
      name: meta.name ?? '',
      unlockedAt: undefined,
    }),

  setNone: () =>
    set({
      status: 'none',
      did: '',
      address: '',
      peerId: '',
      name: '',
      avatarCid: undefined,
      bio: undefined,
      registered: false,
      faucetClaimed: false,
      unlockedAt: undefined,
    }),

  patch: (meta) =>
    set((state) => ({
      did: meta.did ?? state.did,
      address: meta.address ?? state.address,
      peerId: meta.peerId ?? state.peerId,
      name: meta.name ?? state.name,
      avatarCid: meta.avatarCid ?? state.avatarCid,
      bio: meta.bio ?? state.bio,
      registered: meta.registered ?? state.registered,
      faucetClaimed: meta.faucetClaimed ?? state.faucetClaimed,
    })),

  setAutoLockMinutes: (minutes) => set({ autoLockMinutes: minutes }),

  lock: () => {
    container.identity.lock();
    const { did, address, peerId, name } = get();
    logger.info('已锁定');
    set({ status: 'locked', did, address, peerId, name, unlockedAt: undefined });
  },
}));
